import { useState } from 'react'

import { useFormik } from 'formik'
import * as Yup from 'yup'

import { _object } from 'utils/interfaces'

import { Button, InputField } from '../form-inputs'
import { customersService } from 'services/customers.services'

const AddNewCustomerModal = () => {
  const [loading, setLoading] = useState(false)

  const validationSchema = Yup.object({
    first_name: Yup.string().label('First name').required(),
    last_name: Yup.string().label('Lastn ame').required(),
    email: Yup.string().label('Email').required(),
    password: Yup.string().label('Password').min(4).required(),
    phone_number: Yup.string()
      .label('Phone number')
      .matches(/^\d{10}$/, 'Phone number must be exactly 10 digits')
      .required(),
    status: Yup.string().label('Status').required('Status is required'),
    role: Yup.string().label('Role').required('Role is required')
  })

  const formik: any = useFormik({
    initialValues: {
      first_name: '',
      last_name: '',
      phone_number: '',
      status: '',
      role: '',
      email: '',
      password: ''
    },
    enableReinitialize: true,
    validationSchema,
    onSubmit: (values) => {
      setLoading(true)
      customersService.AddCustomer(values).then((results: _object) => {
        if (results.error === false) {
          window.location.reload()
        }
        setLoading(false)
      })
    }
  })

  return (
    <>
      <div
        id="add-new-customer"
        tabIndex={-1}
        aria-hidden="true"
        className="fixed top-0 right-0 left-0 z-50 flex hidden h-[calc(100%-1rem)] max-h-full w-full items-center justify-center overflow-x-hidden overflow-y-auto md:inset-0"
      >
        {/* Background Overlay */}
        <div className="fixed inset-0 bg-black/50"></div>

        {/* Modal Content */}
        <div className="relative z-10 max-h-full w-full max-w-2xl p-4">
          <div className="relative rounded-lg border border-gray-200 bg-white shadow-sm">
            <div className="flex items-center justify-between rounded-t border-b border-gray-200 p-3 px-5">
              <h3 className="text-lg font-semibold">Add New Customer</h3>
              <button
                type="button"
                className="ms-auto inline-flex h-8 w-8 items-center justify-center rounded-lg bg-transparent text-sm text-gray-400"
                data-modal-hide="add-new-customer"
              >
                <svg
                  className="h-3 w-3"
                  aria-hidden="true"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 14 14"
                >
                  <path
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6"
                  />
                </svg>
                <span className="sr-only">Close modal</span>
              </button>
            </div>
            <form className="p-5" onSubmit={formik.handleSubmit}>
              <InputField
                label="First Name"
                name="first_name"
                type="text"
                placeholder="First Name"
                onChange={formik.handleChange}
                value={formik.values.first_name}
                onBlur={formik.handleBlur}
                required={true}
                error={formik.touched.first_name && formik.errors.first_name}
              />
              <InputField
                label="Last Name"
                name="last_name"
                type="text"
                placeholder="Last name"
                onChange={formik.handleChange}
                value={formik.values.last_name}
                onBlur={formik.handleBlur}
                required={true}
                error={formik.touched.last_name && formik.errors.last_name}
              />
              <InputField
                label="Phone Number"
                name="phone_number"
                type="text"
                placeholder="phone_number"
                onChange={formik.handleChange}
                value={formik.values.phone_number}
                onBlur={formik.handleBlur}
                required={true}
                error={formik.touched.phone_number && formik.errors.phone_number}
              />
              <InputField
                label="Email"
                name="email"
                type="email"
                placeholder="Email"
                onChange={formik.handleChange}
                value={formik.values.email}
                onBlur={formik.handleBlur}
                required={true}
                error={formik.touched.email && formik.errors.email}
              />
              <InputField
                label="Password"
                name="password"
                type="password"
                placeholder="Password"
                onChange={formik.handleChange}
                value={formik.values.password}
                onBlur={formik.handleBlur}
                required={true}
                error={formik.touched.password && formik.errors.password}
              />

              <div className="mb-5">
                <label className="mb-2 flex text-sm leading-none font-medium text-gray-700">
                  Status
                </label>
                <div className="flex gap-6">
                  <label className="flex items-center gap-2 text-sm text-gray-700">
                    <input
                      type="radio"
                      name="status"
                      value="active"
                      checked={formik.values.status === 'active'}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      required
                    />
                    Active
                  </label>

                  <label className="flex items-center gap-2 text-sm text-gray-700">
                    <input
                      type="radio"
                      name="status"
                      value="inactive"
                      checked={formik.values.status === 'inactive'}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      required
                    />
                    Inactive
                  </label>
                </div>
              </div>

              <div className="mb-5">
                <label className="mb-2 flex text-sm leading-none font-medium text-gray-700">
                  Role
                </label>
                <div className="flex gap-6">
                  <label className="flex items-center gap-2 text-sm text-gray-700">
                    <input
                      type="radio"
                      name="role"
                      value="admin"
                      checked={formik.values.role === 'admin'}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      required
                    />
                    Admin
                  </label>

                  <label className="flex items-center gap-2 text-sm text-gray-700">
                    <input
                      type="radio"
                      name="role"
                      value="inventory_manager"
                      checked={formik.values.role === 'inventory_manager'}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      required
                    />
                    Inventory Manager
                  </label>
                  <label className="flex items-center gap-2 text-sm text-gray-700">
                    <input
                      type="radio"
                      name="role"
                      value="customer_support"
                      checked={formik.values.role === 'customer_support'}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      required
                    />
                    Customer Support
                  </label>
                  <label className="flex items-center gap-2 text-sm text-gray-700">
                    <input
                      type="radio"
                      name="role"
                      value="logistics_manager"
                      checked={formik.values.role === 'logistics_manager'}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      required
                    />
                    Logistics Manager
                  </label>
                </div>
              </div>

              <div className="mt-3">
                <Button
                  label="Add"
                  type="submit"
                  className="rounded-lg bg-purple-900 py-2 text-center text-sm text-white transition hover:bg-purple-950"
                  loading={loading}
                  disabled={!formik.isValid || !formik.dirty}
                />
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  )
}
export default AddNewCustomerModal
